import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { ErrorState } from '@/components/system/ErrorState';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ApiException } from '@/types/api';
import { ComingSoon } from '@/components/system/ComingSoon';

/**
 * Catches errors thrown while rendering or loading a route — BUILD-PLAN §41.
 *
 * §41 requires an intentional state for each failure mode rather than a
 * single generic error, and forbids exposing raw backend detail. The thrown
 * value is inspected here and mapped to the matching branded state; the
 * original error still reaches the console and error monitoring.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();

  if (import.meta.env.DEV) {
    console.error('Route error:', error);
  }

  if (error instanceof ApiException) {
    switch (error.code) {
      // A stale client reaching a route whose flag was switched off since the
      // page loaded still gets the branded experience, not an error.
      case 'FEATURE_COMING_SOON':
        return <ComingSoon />;

      case 'NOT_FOUND':
        return <NotFoundPage />;

      case 'NETWORK_ERROR':
        return (
          <ErrorState
            title="You appear to be offline"
            description="We couldn’t reach Glanz Luxe. Please check your connection and try again."
            onRetry={() => window.location.reload()}
          />
        );

      case 'UNAUTHENTICATED':
        return (
          <ErrorState
            title="Please sign in"
            description="You need to be signed in to view this page."
          />
        );

      case 'FORBIDDEN':
        return (
          <ErrorState
            title="You don’t have access to this"
            description="Your account doesn’t have permission to view this page."
          />
        );

      case 'RATE_LIMITED':
        return (
          <ErrorState
            title="Just a moment"
            description="You’ve made a lot of requests in a short time. Please try again shortly."
          />
        );

      default:
        break;
    }
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }

  return <ErrorState onRetry={() => window.location.reload()} />;
}
